import { useCallback, useMemo } from 'react';
import { useStoreValue } from 'state/hooks/useStore';
import {
  SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID,
  SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID_V53,
  SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID,
  SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53,
  SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID,
  SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID_V53,
  SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID,
  SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID_V53
} from '@portal/config/base';
import usePageId from '@portal/shared/hooks/usePageId';
import { SHAKER_METAFIELD_KEY } from '@portal/api/constants';
import { STORE_SPECIFIC_UNIQUE_VARIANTS_MAP } from '@portal/constants/products';
import { useQueryGetPromotionalBannerData } from '@portal/state/studio/promotionalBannerData/getPromotionalBannerData';
import { useSubscriptionListPromotionV2 } from '@portal/processes/SubscriptionPageV2/hooks/usePromotionalBannerV2';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { InternalApiRequestError } from 'shared/hooks/useInternalApiRequest/types';
import {
  formatSubscriptionListData,
  getShopifyContractId,
  getShopifyVariantId,
  getSubscriptionVariantIdByQuantity
} from 'shared/utils/subscriptionV2Helpers';

import { SubscriptionsResponseV2 } from '../getSubscriptions/types';
import { useQueryGetSubscriptionDetailsV2 } from '../getSubscriptionDetails';
import { getQueryKeyForNextOrderAddableSubscriptionsV2 } from '../getShopifyShopItemStatus';
import { useMutationAddSubscriptionLineItemV2 } from '../addSubscriptionLineItemV2';
import { useMutationDeleteSubscriptionLineItemV2 } from '../deleteSubscriptionLineItemV2';
import { useQueryGetCustomerMetafields } from '../getCustomerMetafields/getCustomerMetafields';
import { useQueryGetSubscriptionsV2 } from '../getSubscriptions';

export function useMutationHandleFreeShaker(contractIdProp?: number) {
  const store = useStoreValue();
  const queryClient = useQueryClient();
  const fallbackContractId = usePageId();

  const addSubscriptionLineItemMutation = useMutationAddSubscriptionLineItemV2();
  const removeSubscriptionLineItemV2 = useMutationDeleteSubscriptionLineItemV2();

  const { data: subscriptions = [] } = useQueryGetSubscriptionsV2();

  // Handle banner shaker additions
  const { data: promotionalBannerData } = useQueryGetPromotionalBannerData();
  const defaultPromotionalBannerDataV2 = useSubscriptionListPromotionV2({
    subscriptions: formatSubscriptionListData(subscriptions),
    promotionalBannerData
  });
  const subscriptionsListBannerTarget = defaultPromotionalBannerDataV2.targetSubscriptions?.[0];
  const bannerTargetContractId = getShopifyContractId(
    subscriptionsListBannerTarget?.shopify_contract_id || ''
  );

  // Final contract ID depeneding on context
  const contractId = useMemo(() => {
    const fallbackId = fallbackContractId !== -1 ? fallbackContractId : null;
    return contractIdProp || fallbackId || Number(bannerTargetContractId);
  }, [contractIdProp, fallbackContractId, bannerTargetContractId]);

  const { data: subscriptionData } = useQueryGetSubscriptionDetailsV2(contractId);
  const { data: metafields } = useQueryGetCustomerMetafields();

  const shakerId = useMemo(() => {
    const id = STORE_SPECIFIC_UNIQUE_VARIANTS_MAP[store].shaker;
    if (!id) return '';

    return getSubscriptionVariantIdByQuantity(String(id), store, 1);
  }, [store]);

  const isSingleSubVariant = useCallback(
    (lineId: string) =>
      Object.values([
        SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID,
        SHOPIFY_AG1_POUCH_SINGLE_SUB_VARIANT_ID_V53,
        SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID,
        SHOPIFY_TRAVEL_PACK_SINGLE_SUB_VARIANT_ID_V53
      ]).some((variant) => Object.values(variant[store]).includes(lineId)),
    [store]
  );

  const isDoubleSubVariant = useCallback(
    (lineId: string) =>
      Object.values([
        SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID,
        SHOPIFY_AG1_POUCH_DOUBLE_SUB_VARIANT_ID_V53,
        SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID,
        SHOPIFY_TRAVEL_PACK_DOUBLE_SUB_VARIANT_ID_V53
      ]).some((variant) => Object.values(variant[store]).includes(lineId)),
    [store]
  );

  const checkFreeShakerEligibility = useCallback(
    (initialVariantId: string, newVariantId: string, quantity: number) => {
      if (!subscriptionData) {
        throw new Error('Invalid subscription data');
      }
      // Check if customer previousy received a shaker
      const shakerMetafield = metafields?.find(
        (metafield) => metafield.key === SHAKER_METAFIELD_KEY
      );
      const isMetafieldEligible = !shakerMetafield || shakerMetafield.value === 'false';
      // Check if original subscription state is eligible
      const isSubscriptionEligible = subscriptionData.subscription_only_items.some((line) => {
        const lineId = getShopifyVariantId(line.product.shopify_variant_id);
        return isSingleSubVariant(lineId) && line.quantity === 1;
      });
      const subscriptionMissingFreeShaker = !subscriptionData.next_order_only_items.some(
        (line) => String(line.product.shopify_variant_id) === shakerId
      );

      // Check if non-modified sub items are eligible for shaker
      const subscriptionWillContainEligibleProducts = subscriptionData.subscription_only_items.some(
        (line) => {
          const lineId = getShopifyVariantId(line.product.shopify_variant_id);
          // Skip currently modified item
          if (lineId === initialVariantId) return false;
          return isDoubleSubVariant(lineId) && line.quantity > 1;
        }
      );

      const isValidShaker = !!shakerId;
      const shouldAddShaker =
        isValidShaker &&
        isMetafieldEligible &&
        isSubscriptionEligible &&
        subscriptionMissingFreeShaker &&
        isDoubleSubVariant(newVariantId) &&
        quantity > 1;
      const shouldRemoveShaker =
        isValidShaker &&
        !subscriptionWillContainEligibleProducts &&
        isSingleSubVariant(newVariantId) &&
        quantity <= 1;

      return {
        shouldAddShaker,
        shouldRemoveShaker
      };
    },
    [shakerId, metafields, isSingleSubVariant, isDoubleSubVariant, subscriptionData]
  );

  return {
    checkFreeShakerEligibility,
    addShaker: useMutation<any, InternalApiRequestError, any>({
      mutationFn: async () => {
        await addSubscriptionLineItemMutation.mutateAsync({
          contractId,
          body: {
            shopify_product_variant_id: shakerId,
            quantity: 1
          },
          isOneTime: true
        });
      },
      onSuccess: () => {
        queryClient.setQueryData(
          getQueryKeyForNextOrderAddableSubscriptionsV2(shakerId),
          (addableSubscriptions: SubscriptionsResponseV2) =>
            addableSubscriptions?.filter(
              (subscription) =>
                getShopifyContractId(subscription.shopify_contract_id) !== contractId.toString()
            )
        );
      }
    }),
    removeShaker: useMutation<any, InternalApiRequestError, any>({
      mutationFn: async () => {
        const shaker = subscriptionData?.next_order_only_items.find(
          (item) => item.product.shopify_variant_id === shakerId
        );
        if (shaker) {
          await removeSubscriptionLineItemV2.mutateAsync({
            contractId,
            lineItemId: getShopifyVariantId(shaker.shopify_line_item_id)
          });
        }
      }
    })
  };
}
